import { Link } from "react-router";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "@/lib/i18n";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  Menu,
  User,
  Settings,
  LogOut,
  Wand2,
  LayoutDashboard,
  Users,
  CalendarDays,
  Mail,
  Bookmark,
  CreditCard,
  Shield,
} from "lucide-react";
import { getInitials } from "@/lib/utils";
import { NavLink } from "react-router";
import { cn } from "@/lib/utils";
import { NotificationPanel } from "@/components/NotificationPanel";

const mobileNavItems = [
  { labelKey: "nav.dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { labelKey: "nav.giftWizard", icon: Wand2, path: "/wizard" },
  { labelKey: "nav.myPeople", icon: Users, path: "/contacts" },
  { labelKey: "nav.calendar", icon: CalendarDays, path: "/calendar" },
  { labelKey: "nav.cards", icon: Mail, path: "/cards" },
  { labelKey: "nav.savedGifts", icon: Bookmark, path: "/saved-gifts" },
  { labelKey: "nav.subscription", icon: CreditCard, path: "/subscription" },
];

export function Navbar() {
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur-xl">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-4">
          {isAuthenticated && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">{t("nav.toggleNav")}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetTitle className="sr-only">{t("nav.navigation")}</SheetTitle>
                <div className="p-6 border-b">
                  <Link to="/dashboard" className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-gift-gradient flex items-center justify-center">
                      <Wand2 className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-lg font-bold gradient-text">
                      Prezntz
                    </span>
                  </Link>
                </div>
                <nav className="p-4 space-y-1">
                  {mobileNavItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )
                      }
                    >
                      <item.icon className="h-5 w-5" />
                      {t(item.labelKey)}
                    </NavLink>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          )}

          <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gift-gradient flex items-center justify-center">
              <Wand2 className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold gradient-text hidden sm:inline">
              Prezntz
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <NotificationPanel />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-9 w-9 rounded-full"
                  >
                    <div className="relative">
                      <Avatar className={cn("h-9 w-9", user?.isAdmin && "ring-2 ring-gift-purple-500/50")}>
                        <AvatarImage
                          src={user?.avatarUrl ?? undefined}
                          alt={user?.name ?? undefined}
                        />
                        <AvatarFallback>
                          {user ? getInitials(user.name) : "U"}
                        </AvatarFallback>
                      </Avatar>
                      {user?.isAdmin && (
                        <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-gift-purple-500 flex items-center justify-center ring-2 ring-background">
                          <Shield className="h-2.5 w-2.5 text-white" />
                        </div>
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user?.name || user?.email}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      <span>{t("nav.profile")}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>{t("nav.settings")}</span>
                    </Link>
                  </DropdownMenuItem>
                  {user?.isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer">
                          <Shield className="mr-2 h-4 w-4" />
                          <span>{t("nav.admin")}</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="cursor-pointer text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t("nav.logOut")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="ghost" asChild>
                <Link to="/login">{t("nav.signIn")}</Link>
              </Button>
              <Button variant="gradient" asChild>
                <Link to="/login">{t("nav.getStarted")}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
